import { useCallback, useEffect, useState } from 'react';

import AdminPageShell from '../AdminPageShell';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { apiRequest } from '../../../services/api';
import {
  downloadAdminParrainageCsv,
  downloadAdminParrainagePdf,
  fetchAdminParrainageOverview,
} from '../../../services/parrainageService';
import { formatMoney } from '../../../utils/currency';

type Overview = {
  synthese?: {
    totalLiens: number;
    totalCommissions: number;
    bonusMoisCdf: number;
    pendingCommissionCdf: number;
    pendingCommissionCount: number;
  };
  topParrains?: {
    idParrain: string;
    filleulCount: number;
    codeParrainage: string;
    nom: string;
    role?: string;
    typeProfilParrain?: string | null;
  }[];
  recentCommissions?: {
    id: string;
    montantCommission: number;
    statut: string;
    typeProfil: string;
    dateCreation: string;
    beneficiaire: { code: string; nom: string };
  }[];
};

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function AcademiaParrainagePanel() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [releaseMsg, setReleaseMsg] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setData((await fetchAdminParrainageOverview()) as Overview);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur chargement parrainage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const s = data?.synthese;

  return (
    <AdminPageShell
      title="Parrainage & référencement"
      description="Suivi des filleuls, commissions, bonus et performance des parrains."
      sideCard={{
        label: 'Liens parrainage',
        value: loading ? '…' : String(s?.totalLiens ?? 0),
        helper: loading
          ? null
          : `${formatMoney(s?.bonusMoisCdf ?? 0, 'CDF')} bonus ce mois`,
        sideLinks: [
          { label: 'Tarification', to: '/admin/tarifs', icon: 'payments' },
          { label: 'Tableau de bord', to: '/admin/dashboard', icon: 'dashboard' },
        ],
      }}
      isLoading={loading}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement du parrainage…"
    >
      {data ? (
        <div className="space-y-6">
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Liens parrainage</p>
              <p className="mt-1 text-2xl font-black text-on-surface">{s?.totalLiens ?? 0}</p>
            </article>
            <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase text-on-surface-variant">Commissions (lignes)</p>
              <p className="mt-1 text-2xl font-black text-on-surface">{s?.totalCommissions ?? 0}</p>
            </article>
            <article className="rounded-xl border border-emerald-200/50 bg-emerald-50/40 p-4">
              <p className="text-xs font-bold uppercase text-emerald-800">Bonus crédités (mois)</p>
              <p className="mt-1 text-xl font-black text-emerald-900">
                {formatMoney(s?.bonusMoisCdf ?? 0, 'CDF')}
              </p>
            </article>
            <article className="rounded-xl border border-amber-200/50 bg-amber-50/40 p-4">
              <p className="text-xs font-bold uppercase text-amber-800">En attente de seuil</p>
              <p className="mt-1 text-xl font-black text-amber-900">
                {formatMoney(s?.pendingCommissionCdf ?? 0, 'CDF')}
              </p>
              <p className="text-xs text-amber-800">{s?.pendingCommissionCount ?? 0} ligne(s)</p>
            </article>
          </section>

          <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-white shadow-sm">
            <header className="flex items-center gap-3 border-b border-outline-variant/20 bg-surface-container-low px-4 py-3">
              <LucideSymbolIcon name="groups" tone="primary" size={22} />
              <div>
                <h2 className="font-bold text-slate-900">Top parrains</h2>
                <p className="text-xs text-on-surface-variant">Par nombre de filleuls</p>
              </div>
            </header>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-container-low text-xs uppercase text-on-surface-variant">
                  <tr>
                    <th className="px-4 py-2 text-left">Parrain</th>
                    <th className="px-4 py-2 text-left">Code</th>
                    <th className="px-4 py-2 text-left">Rôle</th>
                    <th className="px-4 py-2 text-right">Filleuls</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.topParrains ?? []).map((p) => (
                    <tr key={p.idParrain} className="border-t border-outline-variant/15">
                      <td className="px-4 py-2 font-semibold">{p.nom}</td>
                      <td className="px-4 py-2 font-mono text-xs">{p.codeParrainage}</td>
                      <td className="px-4 py-2 text-xs">{p.typeProfilParrain ?? p.role ?? '—'}</td>
                      <td className="px-4 py-2 text-right font-bold">{p.filleulCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(data.topParrains ?? []).length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-on-surface-variant">Aucun parrainage enregistré.</p>
              ) : null}
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-white shadow-sm">
            <header className="flex items-center gap-3 border-b border-outline-variant/20 px-4 py-3">
              <LucideSymbolIcon name="payments" tone="primary" size={22} />
              <h2 className="font-bold text-slate-900">Dernières commissions</h2>
            </header>
            <ul className="divide-y divide-outline-variant/15">
              {(data.recentCommissions ?? []).map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                  <div>
                    <p className="font-semibold">{c.beneficiaire.nom}</p>
                    <p className="text-xs text-on-surface-variant">
                      {c.beneficiaire.code} · {formatDate(c.dateCreation)} · {c.statut}
                    </p>
                  </div>
                  <p className="font-black text-primary">{formatMoney(c.montantCommission, 'CDF')}</p>
                </li>
              ))}
            </ul>
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={exporting != null}
              className="rounded-lg border border-outline-variant/30 bg-white px-4 py-2 text-xs font-bold text-primary disabled:opacity-50"
              onClick={async () => {
                setExporting('csv');
                setReleaseMsg(null);
                try {
                  await downloadAdminParrainageCsv();
                  setReleaseMsg('Export CSV téléchargé.');
                } catch (e) {
                  setReleaseMsg(e instanceof Error ? e.message : 'Export CSV impossible');
                } finally {
                  setExporting(null);
                }
              }}
            >
              {exporting === 'csv' ? 'Export…' : 'Exporter CSV'}
            </button>
            <button
              type="button"
              disabled={exporting != null}
              className="rounded-lg border border-outline-variant/30 bg-white px-4 py-2 text-xs font-bold text-primary disabled:opacity-50"
              onClick={async () => {
                setExporting('pdf');
                setReleaseMsg(null);
                try {
                  await downloadAdminParrainagePdf();
                  setReleaseMsg('Export PDF téléchargé.');
                } catch (e) {
                  setReleaseMsg(e instanceof Error ? e.message : 'Export PDF impossible');
                } finally {
                  setExporting(null);
                }
              }}
            >
              {exporting === 'pdf' ? 'Export…' : 'Exporter PDF'}
            </button>
            <button
              type="button"
              disabled={releasing}
              className="rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
              onClick={async () => {
                setReleasing(true);
                setReleaseMsg(null);
                try {
                  const res = await apiRequest('/admin/parrainage/release-pending', {
                    method: 'POST',
                    withAuth: true,
                  });
                  setReleaseMsg(res?.message ?? 'Commissions débloquées');
                  await load();
                } catch (e) {
                  setReleaseMsg(e instanceof Error ? e.message : 'Erreur');
                } finally {
                  setReleasing(false);
                }
              }}
            >
              Débloquer commissions en attente de seuil
            </button>
            {releaseMsg ? <p className="text-sm text-on-surface-variant">{releaseMsg}</p> : null}
          </div>
          <p className="text-xs text-on-surface-variant">
            Règles de commission et bonus inscription : menu Tarification → section Parrainage.
          </p>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
